import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { motion, useScroll, useSpring } from 'framer-motion';
import { 
  FaArrowLeft, FaClock, FaCalendarAlt, 
  FaCheck, FaLink 
} from 'react-icons/fa';
import { posts } from '../data/posts';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [markdown, setMarkdown] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    const currentPost = posts.find(p => p.slug === slug);
    if (currentPost) {
      setPost(currentPost);
      fetch(currentPost.content)
        .then(response => response.text())
        .then(text => setMarkdown(text));
    }
    window.scrollTo(0, 0);
  }, [slug]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  if (!post) {
    return (
      <div className="min-h-screen pt-32 text-center text-zinc-500">
        <p>Loading article...</p>
      </div>
    );
  }

  // Find adjacent posts for bottom navigation
  const currentIndex = posts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? posts[currentIndex - 1] : null;
  const nextPost = currentIndex < posts.length - 1 ? posts[currentIndex + 1] : null;

  return (
    <div className="min-h-screen pt-28 pb-24 px-4 sm:px-6 select-text">
      
      {/* Top Reading Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-primary dark:bg-dark-primary z-50 origin-left"
        style={{ scaleX }}
      />

      <div className="max-w-4xl mx-auto space-y-10">
        
        {/* Back Link & Share Action */}
        <div className="flex items-center justify-between">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors"
          >
            <FaArrowLeft size={12} />
            <span>Back to Technical Lab</span>
          </Link>

          <button
            onClick={handleCopyLink}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
            title="Copy Article Link"
          >
            {copiedLink ? <FaCheck className="text-emerald-500" size={12} /> : <FaLink size={12} />}
            <span>{copiedLink ? 'Link Copied!' : 'Share'}</span>
          </button>
        </div>

        {/* Article Header Card */}
        <header className="rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md p-6 sm:p-10 space-y-6 shadow-sm">
          <div className="flex items-center flex-wrap gap-3">
            {post.badge && (
              <span className="inline-flex items-center gap-1 text-xs font-mono font-bold px-3 py-1 rounded-full bg-primary/10 text-primary dark:text-dark-primary border border-primary/20">
                {post.badge}
              </span>
            )}
            <div className="flex items-center gap-3 text-xs font-mono text-zinc-400 dark:text-zinc-500">
              <span className="flex items-center gap-1">
                <FaCalendarAlt size={11} />
                <span>{post.date}</span>
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <FaClock size={11} />
                <span>{post.readTime}</span>
              </span>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-zinc-900 dark:text-white leading-[1.15]">
            {post.title}
          </h1>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-200/60 dark:border-zinc-800/60">
            {post.tags.map(tag => (
              <span
                key={tag}
                className="text-xs font-mono px-3 py-1 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200/40 dark:border-zinc-700/40"
              >
                #{tag}
              </span>
            ))}
          </div>
        </header>

        {/* Markdown Content Article Body */}
        <article className="rounded-3xl bg-white/60 dark:bg-zinc-900/60 border border-zinc-200/60 dark:border-zinc-800/60 backdrop-blur-md p-6 sm:p-12 shadow-sm">
          <div className="prose prose-zinc dark:prose-invert max-w-none prose-headings:font-extrabold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-pre:bg-zinc-950 prose-pre:border prose-pre:border-zinc-800 prose-code:text-primary dark:prose-code:text-dark-primary prose-code:font-mono prose-a:text-primary dark:prose-a:text-dark-primary prose-a:no-underline hover:prose-a:underline leading-relaxed">
            <ReactMarkdown>{markdown}</ReactMarkdown>
          </div>
        </article>

        {/* Bottom Adjacent Articles Navigation */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
          {prevPost ? (
            <Link
              to={`/blog/${prevPost.slug}`}
              className="p-5 rounded-2xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 hover:border-primary/50 dark:hover:border-dark-primary/50 backdrop-blur-md transition-all group"
            >
              <div className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                <FaArrowLeft size={10} />
                <span>Previous Note</span>
              </div>
              <div className="text-sm font-bold text-zinc-900 dark:text-white group-hover:text-primary dark:group-hover:text-dark-primary line-clamp-1 transition-colors">
                {prevPost.title}
              </div>
            </Link>
          ) : <div />}

          {nextPost ? (
            <Link
              to={`/blog/${nextPost.slug}`}
              className="p-5 rounded-2xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 hover:border-primary/50 dark:hover:border-dark-primary/50 backdrop-blur-md transition-all text-right group ml-auto w-full"
            >
              <div className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500 uppercase tracking-wider mb-1 flex items-center justify-end gap-1">
                <span>Next Note</span>
                <FaArrowLeft size={10} className="rotate-180" />
              </div>
              <div className="text-sm font-bold text-zinc-900 dark:text-white group-hover:text-primary dark:group-hover:text-dark-primary line-clamp-1 transition-colors">
                {nextPost.title}
              </div>
            </Link>
          ) : <div />}
        </div>

      </div>
    </div>
  );
};

export default BlogPost;
